import { motion, useAnimationControls } from "motion/react";
import { twJoin } from "tailwind-merge";

import { Star01Icon } from "@/components/Icons/Star01Icon";
import { buttonVariants } from "@/design-system/Button";
import { twResolve } from "@/helpers/tw-resolve";
import { useTranslation } from "@/translations";

export interface StarInputProps {
  value: number | undefined;
  onChange: (stars: number) => void;
  disabled?: boolean;
}
const animations = {
  in: {
    opacity: 1,
    scale: 1.25,
  },
  out: {
    opacity: 0,
    scale: 0.75,
  },
  none: {},
};
export function StarInput({ value, onChange, disabled }: StarInputProps): React.ReactNode {
  const { t } = useTranslation();
  const controls = useAnimationControls();

  return (
    <span className={twJoin("group flex gap-space-xs", disabled && "pointer-events-none opacity-50")}>
      {Array(5)
        .fill(undefined)
        .map((_, i) => (
          <button
            className={twResolve(
              buttonVariants({ variant: "ghostSecondary", size: "xl", inset: "compact" }),
              "size-control-xl scale-100 px-0 text-transparent transition-all active:scale-110 can-hover:hover:scale-110",
            )}
            key={i}
            type="button"
            aria-label={t("component.star-input.option-label", { count: i + 1 })}
            aria-pressed={value === i + 1}
            onClick={() => {
              onChange(i + 1);
            }}
            onPointerDown={() => {
              void controls.start((item) => (i === item ? animations.in : animations.none));
            }}
            onPointerUp={() => {
              // for dragging stars
              onChange(i + 1);
              void controls.start((item) => (i === item ? animations.out : animations.none));
            }}
            onMouseOut={() => {
              void controls.start((item) => (i === item ? animations.out : animations.none));
            }}
            disabled={disabled}
          >
            <Star01Icon appearance="solid" size={48} tone={value != null && value > i ? "rating" : "disabled"} />
            <motion.span
              className="absolute inset-0 -z-10 scale-75 rounded-full bg-rating-background opacity-0"
              animate={controls}
              custom={i}
            />
          </button>
        ))}
    </span>
  );
}
